'use client'

import { useEffect, useState } from 'react'

import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useUserAgent } from '@marketplace-web/shared/request'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { userCmpActionEvent } from '../../tracking/events'
import useLoadConsentBanner from '../../hooks/useLoadConsentBanner'
import { isMobile } from '../../utils/is-mobile'

const ConsentBannerTracker = () => {
  const [hasRespondedToConsent, setHasRespondedToConsent] = useState(false)
  const cmpCopyTest = useAbTest('web_cmp_tests_v3_body')
  const userAgent = useUserAgent()
  const translate = useTranslate('consent_banner')

  const isConsentTrackingEnabled = useFeatureSwitch('web_consent_tracking')

  useTrackAbTest(cmpCopyTest, hasRespondedToConsent)
  const { track } = useTracking()

  useLoadConsentBanner(cmpCopyTest?.variant)

  useEffect(() => {
    if (!isConsentTrackingEnabled) return undefined

    let acceptBtn: HTMLElement | null = null
    let rejectBtn: HTMLElement | null = null

    const handleAcceptClick = () => {
      if (hasRespondedToConsent) return

      track(userCmpActionEvent({ action: 'banner_accept_all', isMobile: isMobile(userAgent) }))
      setHasRespondedToConsent(true)
    }

    const handleRejectClick = () => {
      if (hasRespondedToConsent) return

      track(userCmpActionEvent({ action: 'banner_reject_all', isMobile: isMobile(userAgent) }))
      setHasRespondedToConsent(true)
    }

    const observer = new MutationObserver(() => {
      acceptBtn = document.getElementById('onetrust-accept-btn-handler')
      rejectBtn = document.getElementById('onetrust-reject-all-handler')

      if (acceptBtn && rejectBtn) {
        acceptBtn.addEventListener('click', handleAcceptClick)
        rejectBtn.addEventListener('click', handleRejectClick)

        rejectBtn.textContent = translate('decline_all_a')

        observer.disconnect()
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()

      if (acceptBtn) acceptBtn.removeEventListener('click', handleAcceptClick)
      if (rejectBtn) rejectBtn.removeEventListener('click', handleRejectClick)
    }
  }, [hasRespondedToConsent, track, userAgent, isConsentTrackingEnabled, cmpCopyTest, translate])

  return null
}

export default ConsentBannerTracker
